import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

// Components
import LoadingSpinner from './Spinner';
import TooltipPopover from '../TooltipPopover';

// Types
import { TransformedData } from '../../types/editPage';

// Available status options
const statusOptions = [
  { label: 'Active', value: 'Active' },
  { label: 'Historical Access', value: 'HistoricalAccess' },
  { label: 'Inactive', value: 'Inactive' },
  { label: 'In Progress', value: 'InProgress' },
  { label: 'Onboarding', value: 'Onboarding' },
] as const;

interface AccountStatusProps {
  customer: TransformedData;
  isOnboarding: boolean;
  isNewBusiness: boolean;
  enabledPlatforms: string[];
  onUpdate: (customer: TransformedData) => void;
}

function AccountStatusSelector({
  customer,
  isOnboarding,
  isNewBusiness,
  enabledPlatforms,
  onUpdate,
}: AccountStatusProps) {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  const [isStatusChanged, setIsStatusChanged] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(customer.status);
  const [saveWarning, setSaveWarning] = useState('');

  // Handle the status change event
  const handleStatusChange = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = evt.target.value;
    // Available platforms - '1' is insights, '2' is Comp Intel and so on
    const platforms = ['1', '2', '3', '4'];
    // Check if atleast one platform is configured, if not, do not allow to select and save "Active" from the dropdown
    // this is done because when a client without any modules is selected in the Sidebar dropdown the app crashes!
    const hasPlatformConfigured = customer.platform.some((p) =>
      platforms.includes(p)
    );

    const doNotSaveActiveStatus =
      !hasPlatformConfigured && newStatus === 'Active';

    setSaveWarning(
      doNotSaveActiveStatus
        ? 'Warning: Do not make the status Active when no modules configured or only HSA'
        : ''
    );

    setCurrentStatus(newStatus);
    setIsStatusChanged(!doNotSaveActiveStatus && newStatus !== customer.status); // Enable save button if status changed
  };

  // Handle saving the updated status
  const handleSaveStatus = () => {
    setIsLoading(true);
    fetch('update-status/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customer_id: customer.customer_id,
        status: currentStatus,
        enabled_platforms: enabledPlatforms,
      }),
    })
      .then(() => {
        setIsStatusChanged(false);
        onUpdate({ ...customer, status: currentStatus });
        // Status decides whether the account is listed in the sidebar picker.
        queryClient.invalidateQueries(['get_customer_info_sidebar']);
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <div className="flex items-center space-x-4 px-4 py-4">
      <div className="flex items-center">
        <TooltipPopover
          type="info"
          className="-top-2 -right-3"
          side="top"
          complexContent={
            <div className="flex flex-col text-left">
              <span>
                <span className="font-bold">
                  {t(
                    'settings_page.account_management_tab.status.active.title'
                  )}
                  :{' '}
                </span>
                {t(
                  'settings_page.account_management_tab.status.active.description'
                )}
              </span>
              <span>
                <span className="font-bold">
                  {t(
                    'settings_page.account_management_tab.status.historical_access.title'
                  )}
                  :{' '}
                </span>
                {t(
                  'settings_page.account_management_tab.status.historical_access.description'
                )}
              </span>
              <span>
                <span className="font-bold">
                  {t(
                    'settings_page.account_management_tab.status.inactive.title'
                  )}
                  :{' '}
                </span>
                {t(
                  'settings_page.account_management_tab.status.inactive.description'
                )}
              </span>
              <span>
                <span className="font-bold">
                  {t(
                    'settings_page.account_management_tab.status.in_progress.title'
                  )}
                  :{' '}
                </span>
                {t(
                  'settings_page.account_management_tab.status.in_progress.description'
                )}
              </span>
              <span>
                <span className="font-bold">
                  {t(
                    'settings_page.account_management_tab.status.onboarding.title'
                  )}
                  :{' '}
                </span>
                {t(
                  'settings_page.account_management_tab.status.onboarding.description'
                )}
              </span>
            </div>
          }
        >
          <label className="font-bold text-sm">Status:</label>
        </TooltipPopover>
      </div>
      <select
        value={currentStatus}
        onChange={handleStatusChange}
        className="border border-gray-300 rounded p-2 text-sm"
        disabled={isOnboarding}
      >
        {statusOptions.map(({ label, value }) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>
      <button
        onClick={handleSaveStatus}
        className={`text-sm font-bold py-2 px-4 rounded-full items-center ${
          isStatusChanged && !isNewBusiness ? 'bg-green-600' : 'bg-gray-300'
        } text-light`}
        disabled={!isStatusChanged || isLoading || isOnboarding}
      >
        Save Status
      </button>
      {saveWarning && (
        <span className="mt-1 block rounded-md bg-yellow-50 px-3 py-2 text-sm text-yellow-700">
          {saveWarning}
        </span>
      )}

      {isLoading ? <LoadingSpinner /> : null}
    </div>
  );
}

export default AccountStatusSelector;
